import { useState, useRef, useEffect } from 'react';
import { Loader2, Phone, PhoneOff, Mic } from 'lucide-react';
import { motion } from 'framer-motion';
import * as Sentry from '@sentry/react';
import { VoicePoweredOrb } from './VoicePoweredOrb';
import { NetworkMonitor } from '../utils/networkMonitor';
import { ProtocolRingBuffer } from '../utils/protocolRingBuffer';
import { SentryLogger } from '../utils/sentryLogger';
import { useLiveSession } from '../live/useLiveSession';
import { arrayBufferToBase64, calculateRmsVolume, floatTo16BitPCM } from '../live/audioEngine';
import { computeReconnectSchedule, DEFAULT_RECONNECT_POLICY } from '../live/reconnectPolicy';
import { assertStateTransition, type LiveSessionState } from '../live/sessionStateMachine';
import { parseProtocolMessage } from '../live/protocolHandlers';
import { decideReconnectForCloseCode } from '../live/closeCodePolicy';

// TypeScript declarations for Web Speech API
declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: (event: any) => void;
  onerror: (event: any) => void;
  onend: () => void;
}

export interface TranscriptEntry {
  role: 'user' | 'model';
  text: string;
  timestamp: number;
}

interface GeminiLiveWidgetProps {
  apiKey?: string;
  provider?: 'vertex_ai' | 'gemini_api' | 'vertex_ai_direct' | 'backend_bridge';
  transportMode?: 'legacy_direct' | 'backend_mediated';
  accessToken?: string;
  wsUrl?: string;
  gcpProjectId?: string;
  gcpRegion?: string;
  model: string;
  systemInstruction: string;
  scenarioText: string;
  onEnd?: (duration: number, transcript?: TranscriptEntry[]) => void;
  initialCredits: number;
}

type ConnectionStatus = 'disconnected' | 'connecting' | 'connected';

interface WSMetrics {
  totalBytesSent: number;
  totalBytesReceived: number;
  totalMessagesSent: number;
  totalMessagesReceived: number;
  parseErrors: number;
  maxBufferedAmount: number;
  largePayloads: Array<{ size: number; type: string; timestamp: number }>;
}

interface MessageMeta {
  type: string;
  timestamp: number;
}

interface SessionHandleFingerprint {
  prefix: string | null;
  length: number;
  hash: string | null;
}


export function GeminiLiveWidget({
  apiKey,
  provider,
  transportMode,
  accessToken,
  wsUrl,
  gcpProjectId,
  gcpRegion,
  model,
  systemInstruction,
  onEnd,
  initialCredits
}: GeminiLiveWidgetProps) {
  const [status, setStatus] = useState<ConnectionStatus>('disconnected');
  const [isPTTActive, setIsPTTActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [persistentError, setPersistentError] = useState<string | null>(null); // Error that stays visible
  const [reconnectNotice, setReconnectNotice] = useState<string | null>(null);
  const [isRecoveryUiLock, setIsRecoveryUiLock] = useState(false);
  const [recoveryPhase, setRecoveryPhase] = useState<'idle' | 'reconnect' | 'replay_context' | 'awaiting_model_output'>('idle');
  const [startTime, setStartTime] = useState<number | null>(null);
  const [elapsedTime, setElapsedTime] = useState<number>(0);
  const [, setTranscript] = useState<TranscriptEntry[]>([]);
  const [showLowCreditWarning, setShowLowCreditWarning] = useState(false);
  const lowCreditWarningTimerRef = useRef<number | null>(null);
  const autoEndTimerRef = useRef<number | null>(null);
  /**
   * Session-finalization bookkeeping (IMPORTANT)
   * - We can end a session from multiple paths (user clicks End, auto-end on credits, server closes WS).
   * - React state inside a setTimeout/WS callback can be stale due to closures.
   * - We keep "latest" values in refs so finalizeSession always reports correct duration + transcript.
   */
  const hasEndedRef = useRef(false);
  const startTimeRef = useRef<number | null>(null);
  const elapsedTimeRef = useRef<number>(0);
  const transcriptRef = useRef<TranscriptEntry[]>([]);
 
  const wsRef = useRef<WebSocket | null>(null);
  const liveStateRef = useRef<LiveSessionState>('disconnected');
  const audioContextRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const audioQueueRef = useRef<string[]>([]); // Queue of base64 audio chunks
  const isPlayingRef = useRef(false);
  const nextPlayTimeRef = useRef(0); // Track when next audio should start
  
  // Voice Activity Detection state (for UI feedback only)
  const isSpeakingRef = useRef(false);
  const VOLUME_THRESHOLD = 0.01; // Threshold to detect speech vs silence for UI

  // Session resumption management
  const sessionHandleRef = useRef<string | null>(null);
  const isReconnectingRef = useRef(false);
  const reconnectResumeGateRef = useRef<{
    active: boolean;
    setupComplete: boolean;
    resumable: boolean;
    epoch: number | null;
  }>({ active: false, setupComplete: false, resumable: false, epoch: null });

  // Used to detect if the model repeats the initial scripted greeting after a reconnect.
  const firstModelUtteranceRef = useRef<string | null>(null);
  const pendingGoAwayFirstOutputCheckRef = useRef<{
    epoch: number;
    startedAt: number;
    mode: 'resume_retry' | 'fresh_with_replay';
    hadHandleAtStart: boolean;
    logged: boolean;
  } | null>(null);

  const normalizeUtterance = (text: string): string =>
    text
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ')
      .replace(/[.,!?]/g, '');
  
  // Push-to-Talk state (ref for audio processor access)
  const isPTTActiveRef = useRef(false);
  
  // Track if audio capture is in the process of starting to prevent race conditions
  const captureStartingIdRef = useRef<number>(0);

  // Speech Recognition for capturing user input as text
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const interimTranscriptRef = useRef<string>('');

  // Sentry logging and anomaly detection
  const reconnectionAttemptsRef = useRef<number>(0);
  const lastUserActivityAtRef = useRef<number | null>(null);
  const lastModelActivityAtRef = useRef<number | null>(null);
  const lastActivityEndAtRef = useRef<number | null>(null);
  const noResponseAfterActivityEndReportedRef = useRef<boolean>(false);
  const lastWsCloseInfoRef = useRef<{ code: number; reason: string; timestamp: string } | null>(null);
  const endReasonRef = useRef<'user_click' | 'credits_exhausted' | 'server_disconnect' | 'error' | 'unknown'>('unknown');
  const botSilenceReportedRef = useRef<boolean>(false);
  const shouldStayConnectedRef = useRef<boolean>(false);
  const reconnectTimerRef = useRef<number | null>(null);
  const reconnectModeRef = useRef<'resume_retry' | 'fresh_with_replay'>('resume_retry');
  const connectionEpochRef = useRef<number>(0);
  const setupSentAtRef = useRef<number | null>(null);
  const lastMessageMetaRef = useRef<MessageMeta | null>(null);
  const lastCloseInitiatorRef = useRef<'client' | 'remote' | 'unknown'>('unknown');
  const goAwayStateRef = useRef<{ received: boolean; timeLeft?: string; timestamp?: number }>({ received: false });
  const resumptionRetryCountRef = useRef<number>(0);
  const awaitingResumptionUpdateRef = useRef<boolean>(false);
  const resumptionUpdateTimerRef = useRef<number | null>(null);
  const replayTurnsRef = useRef<number>(0);
  const lastReconnectDelayMsRef = useRef<number>(0);
  const lastRecoveryStageRef = useRef<'resume_retry' | 'fresh_with_replay'>('resume_retry');
  const isRecoveryUiLockRef = useRef<boolean>(false);
  const waitingForFirstModelOutputRef = useRef<boolean>(false);
  const sentryLoggerRef = useRef<SentryLogger | null>(null);
  const protocolBufferRef = useRef<ProtocolRingBuffer | null>(null);
  const networkMonitorRef = useRef<NetworkMonitor | null>(null);
  const wsMetricsRef = useRef<WSMetrics>({
    totalBytesSent: 0,
    totalBytesReceived: 0,
    totalMessagesSent: 0,
    totalMessagesReceived: 0,
    parseErrors: 0,
    maxBufferedAmount: 0,
    largePayloads: [],
  });
  const { transport, transcriptStoreRef } = useLiveSession({
    mode: transportMode,
    apiKey,
    provider,
    accessToken,
    wsUrl,
    gcpProjectId,
    gcpRegion,
    model,
  });

  const MAX_RECONNECT_ATTEMPTS = DEFAULT_RECONNECT_POLICY.maxAttempts;
  const MAX_RESUMPTION_RETRIES = 3;
  const RESUMPTION_UPDATE_TIMEOUT_MS = 4000;
  const LARGE_PAYLOAD_THRESHOLD_BYTES = 100000;

  const transitionLiveState = (nextState: LiveSessionState) => {
    assertStateTransition(liveStateRef.current, nextState);
    liveStateRef.current = nextState;
    if (nextState === 'disconnected' || nextState === 'terminated') {
      setStatus('disconnected');
      return;
    }
    if (nextState === 'connecting' || nextState === 'recovering') {
      setStatus('connecting');
      return;
    }
    setStatus('connected');
  };

  const safeTransitionLiveState = (nextState: LiveSessionState) => {
    try {
      transitionLiveState(nextState);
    } catch {
      liveStateRef.current = nextState;
      if (nextState === 'active') {
        setStatus('connected');
      } else if (nextState === 'connecting' || nextState === 'recovering') {
        setStatus('connecting');
      } else {
        setStatus('disconnected');
      }
    }
  };

  const hashValue = (value: string | null | undefined): string | null => {
    if (!value) return null;
    let hash = 0;
    for (let i = 0; i < value.length; i++) {
      hash = (hash * 31 + value.charCodeAt(i)) >>> 0;
    }
    return hash.toString(16);
  };

  const fingerprintHandle = (value: string | null | undefined): SessionHandleFingerprint => ({
    prefix: value ? value.substring(0, 20) : null,
    length: value ? value.length : 0,
    hash: hashValue(value),
  });

  const estimatePayloadSize = (payload: unknown): number => {
    if (typeof payload === 'string') return payload.length;
    try {
      return JSON.stringify(payload).length;
    } catch {
      return 0;
    }
  };

  const updateWsMetrics = (direction: 'inbound' | 'outbound', payload: unknown, ws?: WebSocket | null) => {
    const metrics = wsMetricsRef.current;
    const size = estimatePayloadSize(payload);
    if (direction === 'outbound') {
      metrics.totalMessagesSent += 1;
      metrics.totalBytesSent += size;
      const buffered = ws?.bufferedAmount ?? 0;
      metrics.maxBufferedAmount = Math.max(metrics.maxBufferedAmount, buffered);
      if (size >= LARGE_PAYLOAD_THRESHOLD_BYTES) {
        metrics.largePayloads.push({ size, type: 'outbound', timestamp: Date.now() });
      }
    } else {
      metrics.totalMessagesReceived += 1;
      metrics.totalBytesReceived += size;
      if (size >= LARGE_PAYLOAD_THRESHOLD_BYTES) {
        metrics.largePayloads.push({ size, type: 'inbound', timestamp: Date.now() });
      }
    }
    if (metrics.largePayloads.length > 20) {
      metrics.largePayloads = metrics.largePayloads.slice(-20);
    }
  };

  const resetWsMetrics = () => {
    wsMetricsRef.current = {
      totalBytesSent: 0,
      totalBytesReceived: 0,
      totalMessagesSent: 0,
      totalMessagesReceived: 0,
      parseErrors: 0,
      maxBufferedAmount: 0,
      largePayloads: [],
    };
  };

  const getWsMetricsSnapshot = () => {
    const metrics = wsMetricsRef.current;
    return {
      ...metrics,
      avgSendSize: metrics.totalMessagesSent
        ? Math.round(metrics.totalBytesSent / metrics.totalMessagesSent)
        : 0,
      avgReceiveSize: metrics.totalMessagesReceived
        ? Math.round(metrics.totalBytesReceived / metrics.totalMessagesReceived)
        : 0,
    };
  };

  const logTimeline = (event: string, details?: Record<string, unknown>) => {
    sentryLoggerRef.current?.logEvent(event, details);
  };

  const logProtocol = (direction: 'inbound' | 'outbound', payload: unknown, ws?: WebSocket | null) => {
    protocolBufferRef.current?.log(direction, payload, {
      readyState: ws?.readyState,
      bufferedAmount: ws?.bufferedAmount,
    });
  };

  const sendWsJson = (ws: WebSocket, payload: unknown) => {
    const serialized = JSON.stringify(payload);
    updateWsMetrics('outbound', serialized, ws);
    logProtocol('outbound', payload, ws);
    ws.send(serialized);
  };

  const buildLiveWsUrl = (): string => {
    return transport.buildWebSocketUrl();
  };

  const resolveSetupModel = (): string => {
    return transport.resolveSetupModel();
  };

  // Build session context for Sentry logging
  const buildSessionContext = () => {
    const now = Date.now();
    const base = startTimeRef.current ?? startTime;
    const durationSec = base ? Math.floor((now - base) / 1000) : 0;
    const transcript = transcriptRef.current;
    
    // Truncate transcript to avoid size limits (max 20k chars)
    const transcriptText = JSON.stringify(transcript);
    const truncatedTranscript = transcriptText.length > 20000 
      ? transcriptText.substring(0, 20000) + '...[truncated]'
      : transcriptText;

    // Get last few messages for context
    const lastMessages = transcript.slice(-5).map(entry => ({
      role: entry.role,
      text: entry.text.substring(0, 200), // Truncate long messages
      timestamp: new Date(entry.timestamp).toISOString()
    }));

    return {
      durationSec,
      initialCredits,
      totalMessages: transcript.length,
      userMessages: transcript.filter(e => e.role === 'user').length,
      modelMessages: transcript.filter(e => e.role === 'model').length,
      lastMessages,
      transcriptFullLength: transcriptText.length,
      transcript: truncatedTranscript,
      wsState: wsRef.current?.readyState,
      lastWsClose: lastWsCloseInfoRef.current,
      reconnectionAttempts: reconnectionAttemptsRef.current,
      hasSessionHandle: !!sessionHandleRef.current,
      sessionHandlePrefix: sessionHandleRef.current?.substring(0, 20),
      endReason: endReasonRef.current,
      isPTTActive: isPTTActiveRef.current,
      isReconnecting: isReconnectingRef.current,
      connectionEpoch: connectionEpochRef.current,
      recoveryStage: lastRecoveryStageRef.current,
      reconnectDelayMs: lastReconnectDelayMsRef.current,
      replayTranscriptTurns: replayTurnsRef.current,
      setupSentAt: setupSentAtRef.current,
      goAwayState: goAwayStateRef.current,
      wsMetrics: getWsMetricsSnapshot(),
      lastMessageMeta: lastMessageMetaRef.current,
      closeInitiator: lastCloseInitiatorRef.current,
    };
  };

  // Log session anomaly to Sentry with full context
  const logSessionAnomaly = (
    type: string, 
    level: 'error' | 'warning' | 'info' = 'error', 
    extra?: Record<string, any>
  ) => {
    try {
      Sentry.withScope((scope) => {
        const correlation = sentryLoggerRef.current?.getCorrelationIds();
        const timeline = sentryLoggerRef.current?.getTimeline().slice(-30) ?? [];
        const protocol = protocolBufferRef.current?.getRecent(60) ?? [];
        const networkState = networkMonitorRef.current?.getCurrentState(
          audioContextRef.current,
          mediaStreamRef.current
        );
        const networkHistory = networkMonitorRef.current?.getStateHistory().slice(-20) ?? [];

        scope.setTag('component', 'GeminiLiveWidget');
        scope.setTag('anomaly_type', type);
        if (correlation?.conversationId) {
          scope.setTag('conversation_id', correlation.conversationId);
        }
        if (correlation?.connectionEpoch != null) {
          scope.setTag('connection_epoch', String(correlation.connectionEpoch));
        }
        scope.setLevel(level);
        
        // Add full session context
        scope.setContext('session', buildSessionContext());
        scope.setContext('correlation', (correlation ?? {}) as Record<string, unknown>);
        scope.setContext('timeline', { events: timeline });
        scope.setContext('protocol_buffer', { events: protocol });
        scope.setContext('network_state', (networkState ?? {}) as Record<string, unknown>);
        scope.setContext('network_history', { events: networkHistory });
        scope.setContext('ws_metrics', getWsMetricsSnapshot());
        
        // Add anomaly-specific details
        if (extra) {
          scope.setContext('anomaly', extra);
        }
        
        Sentry.captureMessage(`Live session anomaly: ${type}`, level);
      });
    } catch (err) {
      console.error('Failed to log anomaly to Sentry:', err);
    }
  };

  // Initialize Web Speech API for transcript capture
  const initSpeechRecognition = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      console.warn('Web Speech API not supported in this browser');
      return null;
    }

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'de-DE'; // German language for FSP exam
    recognition.maxAlternatives = 1;

    recognition.onresult = (event: any) => {
      let interimTranscript = '';
      let finalTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      if (finalTranscript) {
        // Add final transcript to state
        // - We update both React state and the ref to avoid stale values in auto-end callbacks.
        const newEntry = {
          role: 'user' as const,
          text: finalTranscript.trim(),
          timestamp: Date.now()
        };
        setTranscript(prev => {
          const updated = [...prev, newEntry];
          transcriptRef.current = updated;
          transcriptStoreRef.current.replace(updated);
          return updated;
        });
        console.log('User said:', finalTranscript.trim());
        
        // Update user activity timestamp for silence detection
        lastUserActivityAtRef.current = Date.now();
      }
      
      interimTranscriptRef.current = interimTranscript;
    };

    recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error);
      if (event.error !== 'no-speech' && event.error !== 'aborted') {
        setError(`Speech recognition error: ${event.error}`);
      }
    };

    recognition.onend = () => {
      // Auto-restart if PTT is still active
      if (isPTTActiveRef.current && status === 'connected') {
        try {
          recognition.start();
        } catch (e) {
          console.log('Recognition restart skipped:', e);
        }
      }
    };

    return recognition;
  };

  // Start speech recognition
  const startSpeechRecognition = () => {
    if (!recognitionRef.current) {
      recognitionRef.current = initSpeechRecognition();
    }
    
    if (recognitionRef.current) {
      try {
        recognitionRef.current.start();
        console.log('Speech recognition started');
      } catch (e) {
        console.log('Recognition already started or error:', e);
      }
    }
  };

  // Stop speech recognition
  const stopSpeechRecognition = () => {
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
        console.log('Speech recognition stopped');
      } catch (e) {
        console.log('Recognition stop error:', e);
      }
    }
  };


  // Queue audio chunk for sequential playback
  const queueAudioChunk = (audioData: string) => {
    audioQueueRef.current.push(audioData);
    
    // Start playing if not already playing
    if (!isPlayingRef.current) {
      playNextChunk();
    }
  };

  // Play audio chunks sequentially from queue
  const playNextChunk = async () => {
    if (audioQueueRef.current.length === 0) {
      isPlayingRef.current = false;
      nextPlayTimeRef.current = 0;
      return;
    }

    if (!audioContextRef.current) {
      return;
    }

    isPlayingRef.current = true;
    const audioData = audioQueueRef.current.shift()!;

    try {
      // Decode base64 to Int16Array
      const binaryString = atob(audioData);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const int16Array = new Int16Array(bytes.buffer);

      // Convert Int16 to Float32 for Web Audio API
      const float32Array = new Float32Array(int16Array.length);
      for (let i = 0; i < int16Array.length; i++) {
        float32Array[i] = int16Array[i] / (int16Array[i] < 0 ? 0x8000 : 0x7fff);
      }

      // Create audio buffer - Gemini sends audio at 24kHz
      const audioBuffer = audioContextRef.current.createBuffer(
        1, // mono
        float32Array.length,
        24000 // 24kHz sample rate (matching Gemini's output)
      );
      audioBuffer.getChannelData(0).set(float32Array);

      const audioContext = audioContextRef.current;
      const currentTime = audioContext.currentTime;
      
      // Schedule playback - either now or after previous chunk
      const startTime = Math.max(currentTime, nextPlayTimeRef.current);
      nextPlayTimeRef.current = startTime + audioBuffer.duration;

      // Play audio at scheduled time
      const source = audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.connect(audioContext.destination);
      source.onended = () => {
        // Play next chunk when this one finishes
        playNextChunk();
      };
      source.start(startTime);
      
    } catch (err) {
      console.error('Error playing audio:', err);
      // Try next chunk on error
      playNextChunk();
    }
  };

  // Stop audio playback and clear queue (for interruptions)
  const stopAudioPlayback = () => {
    // Only stop if there's actually audio in the queue
    const queueLength = audioQueueRef.current.length;
    if (queueLength === 0 && !isPlayingRef.current) {
      // Nothing to stop, ignore spurious interruption
      return;
    }
    
    console.log('🛑 Stopping audio playback due to interruption, queue had:', queueLength);
    // Clear the audio queue
    audioQueueRef.current = [];
    // Reset playback state
    isPlayingRef.current = false;
    nextPlayTimeRef.current = 0;
    // Note: We cannot stop currently playing BufferSourceNodes after they've started,
    // but clearing the queue prevents future chunks from playing
  };


  // Start audio capture with VAD
  const startAudioCapture = async () => {
    // Stop any existing capture first to prevent leaks
    stopAudioCapture();
    
    // Create a unique ID for this starting attempt to prevent race conditions
    const startingId = Date.now();
    captureStartingIdRef.current = startingId;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          sampleRate: 16000,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      // If stopAudioCapture was called while we were waiting for the stream, stop it immediately
      if (captureStartingIdRef.current !== startingId) {
        console.log('🛑 Audio capture start was cancelled, stopping stream');
        stream.getTracks().forEach(track => track.stop());
        return;
      }

      mediaStreamRef.current = stream;
      audioContextRef.current = new AudioContext({ sampleRate: 16000 });

      const source = audioContextRef.current.createMediaStreamSource(stream);
      const processor = audioContextRef.current.createScriptProcessor(1024, 1, 1);

      processor.onaudioprocess = (e) => {
        // PTT Mode: Only send audio when PTT is active, not reconnecting, and WebSocket is ready
        if (!isPTTActiveRef.current || isReconnectingRef.current || !wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
          return;
        }

        const inputData = e.inputBuffer.getChannelData(0);
        const volume = calculateRmsVolume(inputData);
        const pcm16 = floatTo16BitPCM(inputData);

        // Update speaking state for UI feedback (VoicePoweredOrb)
        if (volume > VOLUME_THRESHOLD) {
          isSpeakingRef.current = true;
        } else {
          isSpeakingRef.current = false;
        }

        // PTT MODE: Only send audio chunk when button/key is held
        const base64Audio = arrayBufferToBase64(pcm16.buffer as ArrayBuffer);
        
        sendWsJson(wsRef.current, {
          realtime_input: {
            media_chunks: [{
              mime_type: 'audio/pcm',
              data: base64Audio,
            }],
          },
        });
      };

      source.connect(processor);
      processor.connect(audioContextRef.current.destination);
      processorRef.current = processor;

    } catch (err) {
      console.error('Error starting audio capture:', err);
      setError('Failed to access microphone');
    }
  };

  // Stop audio capture
  const stopAudioCapture = () => {
    // Reset the starting ID to cancel any pending startAudioCapture calls
    captureStartingIdRef.current = 0;

    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
    if (audioContextRef.current) {
      // Close the context and nullify it
      const ctx = audioContextRef.current;
      if (ctx.state !== 'closed') {
        ctx.close().catch(err => console.error('Error closing AudioContext:', err));
      }
      audioContextRef.current = null;
    }
    // Clear audio queue and state
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    nextPlayTimeRef.current = 0;
    isSpeakingRef.current = false;
  };

  useEffect(() => {
    sentryLoggerRef.current = new SentryLogger();
    protocolBufferRef.current = new ProtocolRingBuffer();
    networkMonitorRef.current = new NetworkMonitor();
    logTimeline('widget_initialized');
    return () => {
      networkMonitorRef.current?.cleanup();
    };
  }, []);

  // Always stop microphone when we are not in an active call
  useEffect(() => {
    if (status !== 'connected') {
      stopAudioCapture();
      setIsPTTActive(false);
      isPTTActiveRef.current = false;
    }
  }, [status]);

  const clearReconnectTimer = () => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  };

  const clearResumptionUpdateTimer = () => {
    if (resumptionUpdateTimerRef.current) {
      clearTimeout(resumptionUpdateTimerRef.current);
      resumptionUpdateTimerRef.current = null;
    }
    awaitingResumptionUpdateRef.current = false;
  };

  const activateRecoveryUiLock = (
    phase: 'reconnect' | 'replay_context' | 'awaiting_model_output',
    notice: string
  ) => {
    isRecoveryUiLockRef.current = true;
    setIsRecoveryUiLock(true);
    setRecoveryPhase(phase);
    setReconnectNotice(notice);
    if (isPTTActiveRef.current) {
      setIsPTTActive(false);
      isPTTActiveRef.current = false;
      stopSpeechRecognition();
      if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        sendWsJson(wsRef.current, {
          realtimeInput: {
            activityEnd: {}
          }
        });
      }
    }
  };

  const releaseRecoveryUiLock = () => {
    isRecoveryUiLockRef.current = false;
    waitingForFirstModelOutputRef.current = false;
    setIsRecoveryUiLock(false);
    setRecoveryPhase('idle');
    setReconnectNotice(null);
  };

  const isSocketStale = (ws: WebSocket, epoch: number): boolean => {
    return wsRef.current !== ws || connectionEpochRef.current !== epoch;
  };

  const computeReconnectDelay = (attempt: number): number => {
    return computeReconnectSchedule(attempt, DEFAULT_RECONNECT_POLICY).delayMs;
  };

  const buildReplaySummaryFromTranscript = (): { summary: string; turnsUsed: number } | null => {
    const transcript = transcriptRef.current;
    if (!transcript.length) return null;

    const recentTurns = transcript.slice(-8);
    const userTurns = recentTurns.filter(entry => entry.role === 'user').slice(-4);
    const modelTurns = recentTurns.filter(entry => entry.role === 'model').slice(-4);

    const compactTurns = recentTurns.slice(-4).map((entry) => {
      const prefix = entry.role === 'user' ? 'Doctor' : 'Patient';
      return `${prefix}: ${entry.text.substring(0, 280)}`;
    });

    const summary = [
      'You are resuming the SAME live conversation after a brief reconnect.',
      'Continue naturally from the context below.',
      'CRITICAL: Do NOT repeat your initial greeting / first scripted line. Do NOT restart the scenario.',
      `User turns captured: ${userTurns.length}.`,
      `Model turns captured: ${modelTurns.length}.`,
      'Recent exchange snapshot (most recent last):',
      ...compactTurns,
      'Continue from the last turn as if the connection never dropped. If unsure, ask a short clarifying question instead of restarting.'
    ].join('\n');

    return { summary, turnsUsed: recentTurns.length };
  };

  const performFinalDisconnect = (message: string) => {
    shouldStayConnectedRef.current = false;
    isReconnectingRef.current = false;
    clearReconnectTimer();
    clearResumptionUpdateTimer();
    releaseRecoveryUiLock();
    setError(message);
    setPersistentError(`[${new Date().toLocaleTimeString()}] ${message}`);
    safeTransitionLiveState('disconnected');
    stopAudioCapture();
    finalizeSession();
  };

  const scheduleReconnect = (
    reason: string,
    options?: { forceFresh?: boolean; immediate?: boolean; sourceCode?: number }
  ) => {
    if (!shouldStayConnectedRef.current) {
      return;
    }

    if (reconnectTimerRef.current || isReconnectingRef.current) {
      return;
    }

    const nextAttempt = reconnectionAttemptsRef.current + 1;
    if (nextAttempt > MAX_RECONNECT_ATTEMPTS) {
      logSessionAnomaly('reconnect_exhausted', 'error', {
        reason,
        attempts: reconnectionAttemptsRef.current,
        sourceCode: options?.sourceCode,
      });
      performFinalDisconnect('Connection lost permanently. Please start a new session.');
      return;
    }

    if (options?.forceFresh) {
      reconnectModeRef.current = 'fresh_with_replay';
      lastRecoveryStageRef.current = 'fresh_with_replay';
    }

    const delayMs = options?.immediate ? 250 : computeReconnectDelay(nextAttempt);
    lastReconnectDelayMsRef.current = delayMs;
    activateRecoveryUiLock('reconnect', 'AI is reconnecting. Please wait...');
    setError(null);

    logSessionAnomaly('reconnect_scheduled', 'warning', {
      reason,
      nextAttempt,
      delayMs,
      recoveryStage: reconnectModeRef.current,
      sourceCode: options?.sourceCode,
    });
    logTimeline('reconnect_scheduled', {
      reason,
      nextAttempt,
      delayMs,
      forceFresh: options?.forceFresh ?? false,
      sourceCode: options?.sourceCode ?? null,
    });

    reconnectTimerRef.current = window.setTimeout(async () => {
      reconnectTimerRef.current = null;
      await reconnectSession({
        forceFresh: options?.forceFresh,
        trigger: reason,
      });
    }, delayMs);
  };

  const reconnectSession = async (options?: { forceFresh?: boolean; trigger?: string }) => {
    if (!shouldStayConnectedRef.current || isReconnectingRef.current) {
      return;
    }

    const trigger = options?.trigger ?? 'unknown';
    const isGoAwayReconnect = trigger === 'goaway';
    const intendedMode: 'resume_retry' | 'fresh_with_replay' = options?.forceFresh ? 'fresh_with_replay' : 'resume_retry';
    // If we don't have a resumption handle, true session resumption can't restore context.
    // Prefer an immediate replay fallback to preserve continuity.
    const effectiveMode: 'resume_retry' | 'fresh_with_replay' =
      intendedMode === 'resume_retry' && !sessionHandleRef.current ? 'fresh_with_replay' : intendedMode;

    isReconnectingRef.current = true;
    // For GoAway we want this to be as transparent as possible:
    // don't flip status away from 'connected', otherwise our status effect stops the mic
    // and the user notices the interruption.
    if (!isGoAwayReconnect) {
      safeTransitionLiveState('recovering');
    }
    clearReconnectTimer();
    clearResumptionUpdateTimer();
    reconnectionAttemptsRef.current++;
    const reconnectStartTime = Date.now();
    sentryLoggerRef.current?.nextReconnectAttemptId();
    logTimeline('reconnect_start', {
      attempt: reconnectionAttemptsRef.current,
      trigger,
    });

    if (isGoAwayReconnect) {
      // This is the "follow-up" chain for a goaway_received event.
      // Keeping these as info-level anomalies makes it easy to confirm whether the
      // post-GoAway session actually resumed and stayed smooth in production.
      logSessionAnomaly('goaway_reconnect_started', 'info', {
        trigger,
        attempt: reconnectionAttemptsRef.current,
        mode: effectiveMode,
        hasHandle: !!sessionHandleRef.current,
        handle: fingerprintHandle(sessionHandleRef.current),
        fromEpoch: connectionEpochRef.current,
        nextEpoch: connectionEpochRef.current + 1,
      });

      pendingGoAwayFirstOutputCheckRef.current = {
        epoch: -1,
        startedAt: Date.now(),
        mode: effectiveMode,
        hadHandleAtStart: !!sessionHandleRef.current,
        logged: false,
      };
    }

    reconnectModeRef.current = effectiveMode;
    lastRecoveryStageRef.current = effectiveMode;

    reconnectResumeGateRef.current = {
      active: reconnectModeRef.current === 'resume_retry',
      setupComplete: false,
      resumable: false,
      epoch: null,
    };
    if (isGoAwayReconnect) {
      // During GoAway resumption there's a short 2-3s gap where the user is confused.
      // Show a neutral "thinking" notice instead of a reconnect warning.
      activateRecoveryUiLock('awaiting_model_output', 'AI is thinking. Please wait...');
    } else {
      activateRecoveryUiLock('reconnect', 'AI is reconnecting. Please wait...');
    }

    console.log('🔄 Starting reconnection:', {
      attempt: reconnectionAttemptsRef.current,
      mode: reconnectModeRef.current,
      trigger,
      hasHandle: !!sessionHandleRef.current,
    });

    if (reconnectionAttemptsRef.current > 5) {
      logSessionAnomaly('excessive_reconnections', 'warning', {
        count: reconnectionAttemptsRef.current,
        sessionHandle: sessionHandleRef.current?.substring(0, 20),
        recoveryStage: reconnectModeRef.current,
      });
    }

    try {
      // Avoid tearing down mic processing for GoAway; keep audio chain alive.
      if (!isGoAwayReconnect && processorRef.current) {
        processorRef.current.disconnect();
        processorRef.current = null;
      }

      if (wsRef.current) {
        const oldWs = wsRef.current;
        oldWs.onopen = null;
        oldWs.onmessage = null;
        oldWs.onerror = null;
        oldWs.onclose = null;
        if (oldWs.readyState === WebSocket.OPEN || oldWs.readyState === WebSocket.CONNECTING) {
          lastCloseInitiatorRef.current = 'client';
          oldWs.close();
        }
        wsRef.current = null;
      }

      // Reduce perceived "dead air" on GoAway-triggered reconnects.
      await new Promise(resolve => setTimeout(resolve, isGoAwayReconnect ? 30 : 300));

      const ws = new WebSocket(buildLiveWsUrl());
      logTimeline('ws_connect_start', { reconnect: true });
      const epoch = connectionEpochRef.current + 1;
      connectionEpochRef.current = epoch;
      sentryLoggerRef.current?.setConnectionEpoch(epoch);
      wsRef.current = ws;

      if (pendingGoAwayFirstOutputCheckRef.current) {
        pendingGoAwayFirstOutputCheckRef.current.epoch = epoch;
      }

      ws.onopen = async () => {
        if (isSocketStale(ws, epoch)) return;
        logTimeline('ws_open', { reconnect: true, epoch });

        if (isGoAwayReconnect) {
          logSessionAnomaly('goaway_reconnect_ws_open', 'info', {
            epoch,
            attempt: reconnectionAttemptsRef.current,
            mode: reconnectModeRef.current,
          });
        }

        // Live API session resumption: if we pass the last handle back in setup,
        // the server restores prior context and we avoid "fresh session" behavior.
        const sessionResumption =
          reconnectModeRef.current === 'resume_retry'
            ? { handle: sessionHandleRef.current ?? null, transparent: true }
            : { handle: null, transparent: true };

        const setupConfig: any = {
          setup: {
            model: resolveSetupModel(),
            generation_config: {
              response_modalities: 'audio',
              temperature: 0.5,
              speech_config: {
                voice_config: {
                  prebuilt_voice_config: {
                    voice_name: 'Puck'
                  }
                }
              }
            },
            system_instruction: {
              parts: [{ text: systemInstruction }]
            },
            sessionResumption,
            realtime_input_config: {
              automatic_activity_detection: {
                disabled: true
              }
            },
            output_audio_transcription: {},
            tools: []
          }
        };
        setupSentAtRef.current = Date.now();
        logTimeline('setup_sent', { mode: reconnectModeRef.current });
        sendWsJson(ws, setupConfig);

        if (isGoAwayReconnect) {
          logSessionAnomaly('goaway_reconnect_setup_sent', 'info', {
            epoch,
            mode: reconnectModeRef.current,
            sessionResumption: {
              hasHandle: !!(sessionResumption as any)?.handle,
              handle: fingerprintHandle((sessionResumption as any)?.handle),
              transparent: !!(sessionResumption as any)?.transparent,
            },
          });
        }

        if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
          await startAudioCapture();
        } else if (audioContextRef.current.state === 'suspended') {
          await audioContextRef.current.resume();
        }

        isReconnectingRef.current = false;
        safeTransitionLiveState('active');

        awaitingResumptionUpdateRef.current = true;
        resumptionUpdateTimerRef.current = window.setTimeout(() => {
          if (!awaitingResumptionUpdateRef.current || !shouldStayConnectedRef.current) return;
          logSessionAnomaly('session_resumption_update_timeout', 'error', {
            reconnectAttempt: reconnectionAttemptsRef.current,
            connectionEpoch: epoch,
            recoveryStage: reconnectModeRef.current,
          });
          clearResumptionUpdateTimer();
          const reachedLimit = resumptionRetryCountRef.current >= MAX_RESUMPTION_RETRIES;
          scheduleReconnect('session_resumption_update_timeout', {
            forceFresh: reachedLimit,
          });
        }, RESUMPTION_UPDATE_TIMEOUT_MS);
      };

      ws.onmessage = async (event) => {
        if (isSocketStale(ws, epoch)) return;
        try {
          const rawPayload = event.data instanceof Blob ? await event.data.text() : String(event.data);
          const parsed = parseProtocolMessage(rawPayload);
          const message = parsed.message as any;
          updateWsMetrics('inbound', rawPayload, ws);
          logProtocol('inbound', message, ws);
          const messageType = parsed.messageType;
          const receivedAt = Date.now();
          lastMessageMetaRef.current = { type: messageType, timestamp: receivedAt };
          const lastActivityEnd = lastActivityEndAtRef.current;
          if (lastActivityEnd != null && receivedAt >= lastActivityEnd) {
            noResponseAfterActivityEndReportedRef.current = false;
            botSilenceReportedRef.current = false;
          }

          const messageKeys = Object.keys(message);
          if (!messageKeys.includes('serverContent') || messageKeys.some(k => k !== 'serverContent')) {
            console.log('📨 [RECONNECT] Message from server:', messageKeys, message);
          }

          if (message.setupComplete) {
            logTimeline('setup_complete', { reconnect: true, epoch });
            reconnectionAttemptsRef.current = 0;

            if (isGoAwayReconnect) {
              logSessionAnomaly('goaway_reconnect_setup_complete', 'info', {
                epoch,
                mode: reconnectModeRef.current,
              });
            }

            if (reconnectModeRef.current === 'fresh_with_replay') {
              waitingForFirstModelOutputRef.current = true;
              activateRecoveryUiLock('replay_context', 'AI is restoring context. Please wait...');
              const replayPayload = buildReplaySummaryFromTranscript();
              replayTurnsRef.current = replayPayload?.turnsUsed ?? 0;
              if (replayPayload) {
                sendWsJson(ws, {
                  clientContent: {
                    turns: [{
                      role: 'user',
                      parts: [{ text: replayPayload.summary }]
                    }],
                    turnComplete: true
                  }
                });
              }
              activateRecoveryUiLock('awaiting_model_output', 'AI is thinking. Please wait...');
            } else {
              // Resume mode: we sent setup with the previous handle; server replied setupComplete.
              // The server may not send a new SessionResumptionUpdate when resuming (it only sends
              // it for fresh sessions). So treat setupComplete as resumption success and stop waiting.
              waitingForFirstModelOutputRef.current = false;
              reconnectResumeGateRef.current.setupComplete = true;
              reconnectResumeGateRef.current.resumable = true;
              reconnectResumeGateRef.current.epoch = epoch;
              clearResumptionUpdateTimer();
              releaseRecoveryUiLock();
            }
          }

          if (message.serverContent?.interrupted) {
            if (!isReconnectingRef.current && (audioQueueRef.current.length > 0 || isPlayingRef.current)) {
              stopAudioPlayback();
            }
          }

          if (message.serverContent?.modelTurn?.parts) {
            let hasAudioChunk = false;
            message.serverContent.modelTurn.parts.forEach((part: any) => {
              if (part.inlineData?.mimeType?.startsWith('audio/pcm') && part.inlineData.data) {
                hasAudioChunk = true;
                queueAudioChunk(part.inlineData.data);
              }
            });
            if (hasAudioChunk && waitingForFirstModelOutputRef.current) {
              logTimeline('first_audio_chunk', { reconnect: true, epoch });
              releaseRecoveryUiLock();
            }
            if (hasAudioChunk) {
              lastModelActivityAtRef.current = Date.now();
              botSilenceReportedRef.current = false;
            }
          }

          if (message.serverContent?.outputTranscription?.text) {
            const transcriptText = message.serverContent.outputTranscription.text.trim();
            if (transcriptText) {
              const pending = pendingGoAwayFirstOutputCheckRef.current;
              if (pending && !pending.logged && pending.epoch === epoch) {
                const baseline = firstModelUtteranceRef.current;
                const repeatedGreetingDetected =
                  !!baseline && normalizeUtterance(baseline) === normalizeUtterance(transcriptText);

                logSessionAnomaly('goaway_post_reconnect_first_output', 'info', {
                  epoch,
                  mode: pending.mode,
                  resumedSession: reconnectResumeGateRef.current.resumable,
                  replayTurnsUsed: replayTurnsRef.current,
                  hadHandleAtStart: pending.hadHandleAtStart,
                  reconnectDelayMs: lastReconnectDelayMsRef.current,
                  timeSinceReconnectStartMs: Date.now() - pending.startedAt,
                  repeatedGreetingDetected,
                  firstOutputSnippet: transcriptText.substring(0, 200),
                  baselineFirstUtteranceSnippet: baseline?.substring(0, 200) ?? null,
                });
                pendingGoAwayFirstOutputCheckRef.current = null;
              }

              if (!firstModelUtteranceRef.current) {
                firstModelUtteranceRef.current = transcriptText;
              }

              const newEntry = {
                role: 'model' as const,
                text: transcriptText,
                timestamp: Date.now()
              };
              setTranscript(prev => {
                const updated = [...prev, newEntry];
                transcriptRef.current = updated;
                transcriptStoreRef.current.replace(updated);
                return updated;
              });
              lastModelActivityAtRef.current = Date.now();
              botSilenceReportedRef.current = false;
              if (waitingForFirstModelOutputRef.current) {
                logTimeline('first_transcript', { reconnect: true, epoch });
                releaseRecoveryUiLock();
              }
            }
          }

          if (message.sessionResumptionUpdate) {
            clearResumptionUpdateTimer();
            const update = message.sessionResumptionUpdate;
            const handle = update.newHandle || update.new_handle;
            const resumable = update.resumable === true && handle && handle.length > 0;
            const previousHandle = sessionHandleRef.current;
            const timeSinceSetupSentMs = setupSentAtRef.current ? Date.now() - setupSentAtRef.current : null;
            logTimeline('session_resumption_update', {
              reconnect: true,
              epoch,
              resumable: update.resumable,
              hasHandle: !!handle,
            });

            if (resumable) {
              sessionHandleRef.current = handle;
              reconnectionAttemptsRef.current = 0;
              resumptionRetryCountRef.current = 0;
              reconnectModeRef.current = 'resume_retry';
              lastRecoveryStageRef.current = 'resume_retry';
              console.log('✅ [RECONNECT] Session handle updated:', {
                previous: previousHandle ? `${previousHandle.substring(0, 20)}...` : 'null',
                new: `${handle.substring(0, 20)}...`,
              });

              if (isGoAwayReconnect) {
                logSessionAnomaly('goaway_resumption_success', 'info', {
                  epoch,
                  resumable: true,
                  previousHandle: fingerprintHandle(previousHandle),
                  newHandle: fingerprintHandle(handle),
                  timeSinceSetupSentMs,
                });
              }

              // Mark resumption as successful; if setup already completed we can fully unlock now.
              reconnectResumeGateRef.current.resumable = true;
              if (reconnectResumeGateRef.current.active && reconnectResumeGateRef.current.setupComplete) {
                releaseRecoveryUiLock();
              }
            } else if (reconnectModeRef.current === 'resume_retry') {
              resumptionRetryCountRef.current += 1;

              if (isGoAwayReconnect) {
                logSessionAnomaly('goaway_resumption_failed', 'warning', {
                  epoch,
                  resumable: false,
                  updateResumableFlag: update.resumable,
                  hasHandle: !!handle,
                  previousHandle: fingerprintHandle(previousHandle),
                  newHandle: fingerprintHandle(handle),
                  retryCount: resumptionRetryCountRef.current,
                  timeSinceSetupSentMs,
                });
              }

              logSessionAnomaly('session_resumption_failed', 'error', {
                resumable: update.resumable,
                hasNewHandle: !!update.newHandle,
                hasNew_handle: !!update.new_handle,
                previousHandle: fingerprintHandle(previousHandle),
                newHandle: fingerprintHandle(handle),
                reconnectAttempt: reconnectionAttemptsRef.current,
                retryCount: resumptionRetryCountRef.current,
                recoveryStage: reconnectModeRef.current,
                timeSinceSetupSentMs,
              });

              const reachedRetryLimit = resumptionRetryCountRef.current >= MAX_RESUMPTION_RETRIES;
              scheduleReconnect('session_resumption_failed', {
                forceFresh: reachedRetryLimit,
                immediate: true,
              });
              if (reachedRetryLimit) {
                activateRecoveryUiLock('replay_context', 'AI is restoring context. Please wait...');
              }
            } else {
              logSessionAnomaly('fresh_session_resumption_update_unusable', 'warning', {
                resumable: update.resumable,
                hasNewHandle: !!update.newHandle,
                hasNew_handle: !!update.new_handle,
                reconnectAttempt: reconnectionAttemptsRef.current,
                recoveryStage: reconnectModeRef.current,
                previousHandle: fingerprintHandle(previousHandle),
                newHandle: fingerprintHandle(handle),
                timeSinceSetupSentMs,
              });
            }
          }

          if (message.goAway && shouldStayConnectedRef.current) {
            const timeLeft = message.goAway.timeLeft;
            goAwayStateRef.current = { received: true, timeLeft, timestamp: Date.now() };
            logTimeline('goaway_received', { timeLeft, reconnect: true, epoch });
            logSessionAnomaly('goaway_received', 'info', {
              timeLeft,
              reconnecting: isReconnectingRef.current,
              hasSessionHandle: !!sessionHandleRef.current,
              recoveryStage: reconnectModeRef.current,
            });
            if (!isReconnectingRef.current) {
              await reconnectSession({ trigger: 'goaway' });
            }
          }

        } catch (err) {
          wsMetricsRef.current.parseErrors += 1;
          logProtocol('inbound', { parseError: true }, ws);
          console.error('Error processing reconnect message:', err);
        }
      };

      ws.onerror = (error) => {
        if (isSocketStale(ws, epoch)) return;
        logSessionAnomaly('ws_reconnect_error', 'error', {
          errorType: error.type,
          timestamp: new Date().toISOString(),
          reconnectAttempt: reconnectionAttemptsRef.current,
          connectionEpoch: epoch,
          recoveryStage: reconnectModeRef.current,
        });
      };

      ws.onclose = (event) => {
        if (isSocketStale(ws, epoch)) return;
        clearResumptionUpdateTimer();
        isReconnectingRef.current = false;
        if (lastCloseInitiatorRef.current === 'unknown') {
          lastCloseInitiatorRef.current = 'remote';
        }
        const now = Date.now();
        const closeContext = {
          initiator: lastCloseInitiatorRef.current,
          goAwayReceived: goAwayStateRef.current.received,
          goAwayTimeLeft: goAwayStateRef.current.timeLeft,
          lastMessageType: lastMessageMetaRef.current?.type,
          lastMessageTimestamp: lastMessageMetaRef.current?.timestamp,
          timeSinceLastMessageMs: lastMessageMetaRef.current ? now - lastMessageMetaRef.current.timestamp : null,
        };
        logTimeline('ws_close', {
          reconnect: true,
          epoch,
          code: event.code,
          initiator: closeContext.initiator,
        });

        lastWsCloseInfoRef.current = {
          code: event.code,
          reason: event.reason || 'no reason',
          timestamp: new Date().toISOString(),
        };

        const reconnectDecision = decideReconnectForCloseCode(
          event.code,
          closeContext.initiator === 'client',
        );
        if (reconnectDecision.shouldReconnect) {
          logSessionAnomaly('ws_reconnect_unexpected_close', 'error', {
            code: event.code,
            reason: event.reason || 'no reason',
            wasClean: event.wasClean,
            reconnectAttempt: reconnectionAttemptsRef.current,
            connectionEpoch: epoch,
            recoveryStage: reconnectModeRef.current,
            decisionReason: reconnectDecision.reason,
            closeContext,
          });
          scheduleReconnect('ws_reconnect_unexpected_close', {
            sourceCode: event.code,
          });
          lastCloseInitiatorRef.current = 'unknown';
          return;
        }

        // Non-retryable close during a reconnect attempt: end cleanly and surface a clear error.
        logSessionAnomaly('ws_reconnect_nonretryable_close', 'error', {
          code: event.code,
          reason: event.reason || 'no reason',
          wasClean: event.wasClean,
          reconnectAttempt: reconnectionAttemptsRef.current,
          connectionEpoch: epoch,
          recoveryStage: reconnectModeRef.current,
          decisionReason: reconnectDecision.reason,
          closeContext,
        });
        const reasonSuffix = event.reason ? `: ${event.reason}` : '';
        performFinalDisconnect(`Connection closed (code ${event.code}${reasonSuffix}). Please start a new session.`);
        lastCloseInitiatorRef.current = 'unknown';
      };

      const reconnectDuration = Date.now() - reconnectStartTime;
      if (reconnectDuration > 10000) {
        logSessionAnomaly('slow_reconnection', 'warning', {
          durationMs: reconnectDuration,
          attempt: reconnectionAttemptsRef.current,
          recoveryStage: reconnectModeRef.current,
        });
      }

    } catch (err) {
      console.error('Reconnection failed:', err);
      isReconnectingRef.current = false;
      logSessionAnomaly('reconnection_exception', 'error', {
        error: err instanceof Error ? err.message : String(err),
        stack: err instanceof Error ? err.stack : undefined,
        attempt: reconnectionAttemptsRef.current,
        hadSessionHandle: !!sessionHandleRef.current,
        recoveryStage: reconnectModeRef.current,
      });
      scheduleReconnect('reconnection_exception');
    }
  };

  // Connect to Gemini
  const handleStart = async () => {
    shouldStayConnectedRef.current = true;
    reconnectModeRef.current = 'resume_retry';
    lastRecoveryStageRef.current = 'resume_retry';
    sessionHandleRef.current = null;
    firstModelUtteranceRef.current = null;
    pendingGoAwayFirstOutputCheckRef.current = null;
    reconnectionAttemptsRef.current = 0;
    resumptionRetryCountRef.current = 0;
    replayTurnsRef.current = 0;
    clearReconnectTimer();
    clearResumptionUpdateTimer();
    releaseRecoveryUiLock();
    safeTransitionLiveState('connecting');
    setError(null);
    endReasonRef.current = 'unknown';
    hasEndedRef.current = false;
    logTimeline('session_start_requested');

    try {
      resetWsMetrics();
      goAwayStateRef.current = { received: false };
      sentryLoggerRef.current?.nextTurnId();
      const ws = new WebSocket(buildLiveWsUrl());
      logTimeline('ws_connect_start', { reconnect: false });
      const epoch = connectionEpochRef.current + 1;
      connectionEpochRef.current = epoch;
      sentryLoggerRef.current?.setConnectionEpoch(epoch);
      wsRef.current = ws;

      ws.onopen = async () => {
        if (isSocketStale(ws, epoch)) return;
        console.log('WebSocket connected');
        logTimeline('ws_open', { reconnect: false, epoch });
        reconnectionAttemptsRef.current = 0;

        // Send setup message WITHOUT context compression (conflicts with session resumption)
        const sessionResumption = { handle: null, transparent: true };
        const setupConfig: any = {
          setup: {
            model: resolveSetupModel(),
            generation_config: {
              response_modalities: 'audio',
              temperature: 0.5,
              speech_config: {
                voice_config: {
                  prebuilt_voice_config: {
                    voice_name: 'Puck'
                  }
                }
              }
            },
            system_instruction: {
              parts: [{ text: systemInstruction }]
            },
            sessionResumption,
            // PTT Mode: Disable automatic activity detection, use manual signals
            realtime_input_config: {
              automatic_activity_detection: {
                disabled: true
              }
            },
            // Enable audio transcription to get the actual spoken text (not thinking/reasoning)
            output_audio_transcription: {},
            tools: [],
            // NOTE: context_window_compression removed for connection stability
            // and causes code 1011 "Internal error encountered" from Gemini API after ~8-10 minutes
            // The API will handle context management automatically

          }
        };

        setupSentAtRef.current = Date.now();
        logTimeline('setup_sent', { mode: 'initial_connect' });
        sendWsJson(ws, setupConfig);

        safeTransitionLiveState('active');
        setReconnectNotice(null);
        await startAudioCapture();
      };

      ws.onmessage = async (event) => {
        if (isSocketStale(ws, epoch)) return;
        try {
          const rawPayload = event.data instanceof Blob ? await event.data.text() : String(event.data);
          const parsed = parseProtocolMessage(rawPayload);
          const message = parsed.message as any;
          updateWsMetrics('inbound', rawPayload, ws);
          logProtocol('inbound', message, ws);
          const messageType = parsed.messageType;
          const receivedAt = Date.now();
          lastMessageMetaRef.current = { type: messageType, timestamp: receivedAt };
          const lastActivityEnd = lastActivityEndAtRef.current;
          if (lastActivityEnd != null && receivedAt >= lastActivityEnd) {
            // We got server traffic after the user's last turn ended.
            noResponseAfterActivityEndReportedRef.current = false;
            botSilenceReportedRef.current = false;
          }
          
          // DEBUG: Log all non-audio messages
          const messageKeys = Object.keys(message);
          if (!messageKeys.includes('serverContent') || messageKeys.some(k => k !== 'serverContent')) {
            console.log('📨 Message from server:', messageKeys, message);
          }

          // After setup complete, send a silent initialization message to trigger session handle
          if (message.setupComplete) {
            logTimeline('setup_complete', { reconnect: false, epoch });
            console.log('✅ Setup complete! Sending initialization message to get session handle...');
            sendWsJson(ws, {
              clientContent: {
                turns: [{
                  role: 'user',
                  parts: [{ text: 'Ready' }]
                }],
                turnComplete: true
              }
            });
          }

          // Handle interruption (user started speaking while model was responding)
          // Handle interruption (user started speaking while model was responding)
          if (message.serverContent?.interrupted) {
            // Only stop playback if we are NOT in the middle of a reconnection AND there's actually audio to stop
            if (!isReconnectingRef.current && (audioQueueRef.current.length > 0 || isPlayingRef.current)) {
              console.log('🚨 Interruption detected - user started speaking');
              stopAudioPlayback();
            } else {
              console.log('🚨 Interruption ignored (reconnecting or no audio playing)');
            }
          }
          
          // Handle server content (audio response)
          if (message.serverContent?.modelTurn?.parts) {
            // Process audio chunks for playback
            message.serverContent.modelTurn.parts.forEach((part: any) => {
              // Check if mimeType starts with 'audio/pcm' (Gemini sends 'audio/pcm;rate=24000')
              if (part.inlineData?.mimeType?.startsWith('audio/pcm') && part.inlineData.data) {
                logTimeline('first_audio_chunk', { reconnect: false, epoch });
                queueAudioChunk(part.inlineData.data);
                lastModelActivityAtRef.current = Date.now();
                botSilenceReportedRef.current = false;
              }
            });
          }
          
          // Handle output transcription (actual spoken text, not thinking/reasoning)
          // This is the text that was actually spoken to the user, transcribed from the audio
          if (message.serverContent?.outputTranscription?.text) {
            const transcriptText = message.serverContent.outputTranscription.text.trim();
            if (transcriptText) {
              console.log('Model said (transcribed):', transcriptText);
              if (!firstModelUtteranceRef.current) {
                firstModelUtteranceRef.current = transcriptText;
              }
              // Add model response to transcript using the actual spoken text
              const newEntry = {
                role: 'model' as const,
                text: transcriptText,
                timestamp: Date.now()
              };
              setTranscript(prev => {
                const updated = [...prev, newEntry];
                transcriptRef.current = updated;
                transcriptStoreRef.current.replace(updated);
                return updated;
              });
              
              // Update model activity timestamp for silence detection
              lastModelActivityAtRef.current = Date.now();
              botSilenceReportedRef.current = false;
              logTimeline('first_transcript', { reconnect: false, epoch });
            }
          }

          // Handle user turn (transcript of what user said)
          if (message.serverContent?.turnComplete) {
            // Turn is complete - could log this if needed
            console.log('Turn complete');
          }

          // Handle session resumption updates
          // CRITICAL: This message may arrive AFTER turn_complete, so never close connection immediately
          if (message.sessionResumptionUpdate) {
            const update = message.sessionResumptionUpdate;
            const previousHandle = sessionHandleRef.current;
            const handle = update.newHandle || update.new_handle;
            const timeSinceSetupSentMs = setupSentAtRef.current ? Date.now() - setupSentAtRef.current : null;
            logTimeline('session_resumption_update', {
              reconnect: false,
              epoch,
              resumable: update.resumable,
              hasHandle: !!handle,
            });
            console.log('🔑 SessionResumptionUpdate received (FULL):', JSON.stringify(update, null, 2));
            console.log('🔍 Keys:', Object.keys(update));
            console.log('🔍 Values:', {
              resumable: update.resumable,
              newHandle: update.newHandle,
              new_handle: update.new_handle,
            });
            
            // Try both camelCase and snake_case (API may use either format)
            if (update.resumable === true && handle && handle.length > 0) {
              sessionHandleRef.current = handle;
              resumptionRetryCountRef.current = 0;
              console.log('✅ Session handle updated:', {
                previous: previousHandle ? `${previousHandle.substring(0, 20)}...` : 'null',
                new: `${handle.substring(0, 20)}...`,
                handleLength: handle.length
              });
            } else {
              console.warn('⚠️ SessionResumptionUpdate incomplete or not resumable:', {
                resumable: update.resumable, 
                hasNewHandle: !!update.newHandle,
                hasNew_handle: !!update.new_handle,
                handleLength: handle ? handle.length : 0
              });
              
              // Log failed session resumption to Sentry
              logSessionAnomaly('session_resumption_failed', 'error', {
                resumable: update.resumable,
                hasNewHandle: !!update.newHandle,
                hasNew_handle: !!update.new_handle,
                previousHandle: fingerprintHandle(previousHandle),
                newHandle: fingerprintHandle(handle),
                reconnectAttempt: reconnectionAttemptsRef.current,
                connectionEpoch: epoch,
                recoveryStage: reconnectModeRef.current,
                timeSinceSetupSentMs,
              });
            }
          }

          // Handle GoAway message (server warning before disconnect)
          if (message.goAway) {
            const timeLeft = message.goAway.timeLeft;
            goAwayStateRef.current = { received: true, timeLeft, timestamp: Date.now() };
            logTimeline('goaway_received', { timeLeft, reconnect: false, epoch });
            console.log(`GoAway received: ${timeLeft} remaining before disconnect`);
            
            // Log GoAway to Sentry for monitoring Gemini API limits
            logSessionAnomaly('goaway_received', 'info', {
              timeLeft,
              reconnecting: isReconnectingRef.current,
              hasSessionHandle: !!sessionHandleRef.current,
            });
            
            // Trigger seamless reconnection before connection closes
            if (!isReconnectingRef.current) {
              console.log('Initiating seamless reconnection...');
              await reconnectSession({ trigger: 'goaway' });
            }
          }

          // Handle tool calls or other message types
          if (message.toolCall) {
            console.log('Tool call received:', message.toolCall);
          }

        } catch (err) {
          wsMetricsRef.current.parseErrors += 1;
          logProtocol('inbound', { parseError: true }, ws);
          console.error('Error processing message:', err);
        }
      };

      ws.onerror = (error) => {
        if (isSocketStale(ws, epoch)) return;
        const errorMessage = 'Connection unstable. Attempting recovery...';
        console.error('WebSocket error:', error);
        console.error('Error details:', {
          type: error.type,
          target: error.target,
          timestamp: new Date().toISOString()
        });
        
        logSessionAnomaly('ws_error', 'error', {
          errorType: (error as Event & { type?: string }).type,
          timestamp: new Date().toISOString(),
          connectionEpoch: epoch,
        });
        setError(errorMessage);
        setReconnectNotice('Reconnecting conversation...');
        scheduleReconnect('ws_error', { immediate: true });
      };

      ws.onclose = (event) => {
        if (isSocketStale(ws, epoch)) {
          console.log('Old WebSocket closed, ignoring cleanup');
          return;
        }
        clearResumptionUpdateTimer();
        if (lastCloseInitiatorRef.current === 'unknown') {
          lastCloseInitiatorRef.current = 'remote';
        }
        const now = Date.now();
        const closeContext = {
          initiator: lastCloseInitiatorRef.current,
          goAwayReceived: goAwayStateRef.current.received,
          goAwayTimeLeft: goAwayStateRef.current.timeLeft,
          lastMessageType: lastMessageMetaRef.current?.type,
          lastMessageTimestamp: lastMessageMetaRef.current?.timestamp,
          timeSinceLastMessageMs: lastMessageMetaRef.current ? now - lastMessageMetaRef.current.timestamp : null,
        };
        logTimeline('ws_close', {
          reconnect: false,
          epoch,
          code: event.code,
          initiator: closeContext.initiator,
        });

        const closeInfo = {
          code: event.code,
          reason: event.reason || 'No reason provided',
          wasClean: event.wasClean,
          timestamp: new Date().toISOString()
        };
        
        console.log('WebSocket closed:', closeInfo);
        
        // Log detailed close information
        let closeMessage = `WebSocket closed`;
        if (event.code === 1000) {
          closeMessage = 'WebSocket closed normally (code 1000)';
        } else if (event.code === 1001) {
          closeMessage = 'WebSocket closed: endpoint going away (code 1001)';
        } else if (event.code === 1006) {
          closeMessage = 'WebSocket closed abnormally - connection lost (code 1006)';
        } else if (event.code === 1008) {
          closeMessage = 'WebSocket closed: policy violation (code 1008)';
        } else if (event.code === 1011) {
          closeMessage = 'WebSocket closed: server error (code 1011)';
        } else if (event.code === 1012) {
          closeMessage = 'WebSocket closed: service restart (code 1012)';
        } else {
          closeMessage = `WebSocket closed with code ${event.code}`;
        }
        
        if (event.reason) {
          closeMessage += ` - Reason: ${event.reason}`;
        }
        
        const fullErrorMsg = `[${new Date().toLocaleTimeString()}] ${closeMessage} | Clean: ${event.wasClean} | Code: ${event.code}`;
        console.error('Connection closed details:', fullErrorMsg);

        // Store close info for context
        lastWsCloseInfoRef.current = {
          code: event.code,
          reason: event.reason || 'No reason provided',
          timestamp: new Date().toISOString(),
        };

        const reconnectDecision = decideReconnectForCloseCode(
          event.code,
          closeContext.initiator === 'client',
        );
        if (reconnectDecision.shouldReconnect) {
          endReasonRef.current = 'server_disconnect';
          logSessionAnomaly('ws_unexpected_close', 'error', {
            code: event.code,
            reason: event.reason || 'No reason provided',
            wasClean: event.wasClean,
            closeMessage,
            connectionEpoch: epoch,
            recoveryStage: reconnectModeRef.current,
            decisionReason: reconnectDecision.reason,
            closeContext,
          });
          setReconnectNotice('Reconnecting conversation...');
          scheduleReconnect('ws_unexpected_close', { sourceCode: event.code, immediate: true });
          lastCloseInitiatorRef.current = 'unknown';
          return;
        }

        if (shouldStayConnectedRef.current) {
          setPersistentError(fullErrorMsg);
          safeTransitionLiveState('disconnected');
          setReconnectNotice(null);
          stopAudioCapture();
          finalizeSession();
        }
        lastCloseInitiatorRef.current = 'unknown';
      };

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to start session';
      console.error('Failed to start session:', err);
      console.error('Error details:', {
        message: errorMessage,
        stack: err instanceof Error ? err.stack : undefined,
        timestamp: new Date().toISOString()
      });
      setError(errorMessage);
      setPersistentError(`[${new Date().toLocaleTimeString()}] ${errorMessage}`);
      safeTransitionLiveState('disconnected');
      shouldStayConnectedRef.current = false;
    }
  };

  /**
   * Finalize session exactly once.
   * - Computes duration from refs (closure-safe).
   * - Sends latest transcript snapshot via ref (closure-safe).
   * - Used by: manual end, auto-end (credits), and server-side disconnects.
   */
  const finalizeSession = () => {
    if (hasEndedRef.current) return;
    hasEndedRef.current = true;

    // Stop speech recognition
    stopSpeechRecognition();

    // Hide any in-session warnings
    setShowLowCreditWarning(false);

    // Prefer the tracked elapsedTime ref; fall back to startTime ref if needed.
    let duration = elapsedTimeRef.current;
    const base = startTimeRef.current ?? startTime;
    if (!duration && base) {
      duration = Math.floor((Date.now() - base) / 1000);
    }

    // Use transcript ref to get the latest value (not stale closure)
    const finalTranscript = transcriptRef.current;

    // Log short or empty sessions to Sentry
    const MIN_EXPECTED_DURATION_SEC = 30;
    const isShort = duration < MIN_EXPECTED_DURATION_SEC;
    const isEmpty = finalTranscript.length === 0 || 
                    (finalTranscript.length === 1 && finalTranscript[0].text.length < 10);
    
    if (isShort || isEmpty) {
      const level = (endReasonRef.current === 'server_disconnect' || endReasonRef.current === 'error') 
        ? 'error' 
        : 'warning';
      
      logSessionAnomaly('short_or_empty_session', level, {
        duration,
        transcriptLength: finalTranscript.length,
        endReason: endReasonRef.current,
        userMessages: finalTranscript.filter(e => e.role === 'user').length,
        modelMessages: finalTranscript.filter(e => e.role === 'model').length,
      });
    }

    onEnd?.(duration, finalTranscript);
  };

  // End session
  const handleEnd = () => {
    if (hasEndedRef.current) return;
    logTimeline('session_end_requested', { reason: endReasonRef.current });
    shouldStayConnectedRef.current = false;
    isReconnectingRef.current = false;
    releaseRecoveryUiLock();
    clearReconnectTimer();
    clearResumptionUpdateTimer();

    // Set end reason if not already set (e.g., by error or server disconnect)
    if (endReasonRef.current === 'unknown') {
      endReasonRef.current = 'user_click';
    }

    // Clear credit timers
    if (lowCreditWarningTimerRef.current) {
      clearTimeout(lowCreditWarningTimerRef.current);
      lowCreditWarningTimerRef.current = null;
    }
    if (autoEndTimerRef.current) {
      clearTimeout(autoEndTimerRef.current);
      autoEndTimerRef.current = null;
    }

    if (wsRef.current) {
      lastCloseInitiatorRef.current = 'client';
      wsRef.current.close();
      wsRef.current = null;
    }
    stopAudioCapture();
    safeTransitionLiveState('terminated');
    safeTransitionLiveState('disconnected');
    setShowLowCreditWarning(false);

    // Notify parent once with final duration/transcript
    finalizeSession();
  };

  // Push-to-Talk button handlers
  // - handlePTTStart: Activates microphone and signals Gemini to start listening
  // - handlePTTEnd: Deactivates microphone and signals Gemini to start generating response
  const handlePTTStart = () => {
    if (isRecoveryUiLockRef.current) {
      return;
    }
    setIsPTTActive(true);
    isPTTActiveRef.current = true;
    noResponseAfterActivityEndReportedRef.current = false;
    botSilenceReportedRef.current = false;
    
    // Start speech recognition for transcript capture
    startSpeechRecognition();
    
    // Signal start of activity to Gemini API
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      console.log('🎤 PTT Started - Signaling activity start to Gemini');
      sendWsJson(wsRef.current, {
        realtimeInput: {
          activityStart: {}
        }
      });
    }
  };

  const handlePTTEnd = () => {
    const wasActive = isPTTActiveRef.current;
    setIsPTTActive(false);
    isPTTActiveRef.current = false;
    if (!wasActive) {
      return;
    }

    // Fallback signal for Chrome Android (Web Speech can be flaky):
    // treat "PTT released" as end of user turn for watchdog + Sentry logging.
    const endedAt = Date.now();
    lastActivityEndAtRef.current = endedAt;
    lastUserActivityAtRef.current = endedAt;
    noResponseAfterActivityEndReportedRef.current = false;
    
    // Stop speech recognition
    stopSpeechRecognition();
    
    // Signal end of activity to Gemini API so it can start generating response
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      console.log('🎤 PTT Released - Signaling activity end to Gemini');
      sendWsJson(wsRef.current, {
        realtimeInput: {
          activityEnd: {}
        }
      });
    } else {
      logSessionAnomaly('ptt_activity_end_ws_not_open', 'error', {
        wsReadyState: wsRef.current?.readyState ?? null,
        wsBufferedAmount: wsRef.current?.bufferedAmount ?? null,
        reconnecting: isReconnectingRef.current,
        recoveryStage: reconnectModeRef.current,
      });
    }
  };

  // Format elapsed time as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Timer effect - update elapsed time every second when connected
  useEffect(() => {
    let interval: number | null = null;
    
    if (status === 'connected' && startTime === null) {
      // Start the timer
      // - Keep a ref copy so duration can be computed reliably in auto-end callbacks.
      const now = Date.now();
      setStartTime(now);
      startTimeRef.current = now;
    }
    
    if (status === 'connected' && startTime !== null) {
      interval = setInterval(() => {
        // - Use refs as the source of truth for closure safety.
        const base = startTimeRef.current ?? startTime;
        const elapsed = base ? Math.floor((Date.now() - base) / 1000) : 0;
        setElapsedTime(elapsed);
        elapsedTimeRef.current = elapsed;
      }, 1000);
    } else if (status === 'disconnected') {
      // Reset timer when disconnected
      setStartTime(null);
      setElapsedTime(0);
      startTimeRef.current = null;
      elapsedTimeRef.current = 0;
    }
    
    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [status, startTime]);

  // Silence Watchdog - detect when bot doesn't respond after user speaks
  useEffect(() => {
    if (status !== 'connected') return;

    const MODEL_SILENCE_THRESHOLD_MS = 20000; // 20 seconds
    const CHECK_INTERVAL_MS = 5000; // Check every 5 seconds

    const watchdogInterval = setInterval(() => {
      const now = Date.now();
      const lastUser = lastUserActivityAtRef.current;
      const lastModel = lastModelActivityAtRef.current;

      // Check if user spoke after the last model response
      if (lastUser && lastModel && lastUser > lastModel) {
        const silenceDuration = now - lastUser;
        
        // If bot has been silent for too long and we haven't reported it yet
        if (silenceDuration > MODEL_SILENCE_THRESHOLD_MS && !botSilenceReportedRef.current) {
          console.warn(`⚠️ Bot silence detected: ${silenceDuration}ms since user's last message`);
          
          const lastUserMsg = transcriptRef.current.filter(e => e.role === 'user').slice(-1)[0];
          const lastModelMsg = transcriptRef.current.filter(e => e.role === 'model').slice(-1)[0];
          
          logSessionAnomaly('bot_silence', 'error', {
            silenceMs: silenceDuration,
            lastUserText: lastUserMsg?.text.substring(0, 200),
            lastUserTimestamp: lastUserMsg?.timestamp,
            lastModelText: lastModelMsg?.text.substring(0, 200),
            lastModelTimestamp: lastModelMsg?.timestamp,
          });
          
          botSilenceReportedRef.current = true;
          setError('⚠️ Model is not responding. Please try ending and restarting the session.');
        }
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      clearInterval(watchdogInterval);
    };
  }, [status]);

  // Turn-level watchdog: user released PTT but no server traffic arrives afterwards.
  // This does not depend on Web Speech transcripts (important on Chrome Android).
  useEffect(() => {
    if (status !== 'connected') return;

    const NO_RESPONSE_THRESHOLD_MS = 20000; // 20 seconds
    const CHECK_INTERVAL_MS = 5000;

    const interval = window.setInterval(() => {
      const lastEnd = lastActivityEndAtRef.current;
      if (lastEnd == null) return;
      if (noResponseAfterActivityEndReportedRef.current) return;

      const lastInbound = lastMessageMetaRef.current?.timestamp ?? null;
      if (lastInbound != null && lastInbound >= lastEnd) return;

      const deltaMs = Date.now() - lastEnd;
      if (deltaMs < NO_RESPONSE_THRESHOLD_MS) return;

      noResponseAfterActivityEndReportedRef.current = true;
      logSessionAnomaly('no_server_response_after_activity_end', 'error', {
        msSinceActivityEnd: deltaMs,
        lastInboundMeta: lastMessageMetaRef.current,
        wsReadyState: wsRef.current?.readyState ?? null,
        wsBufferedAmount: wsRef.current?.bufferedAmount ?? null,
        reconnecting: isReconnectingRef.current,
        recoveryStage: reconnectModeRef.current,
      });
      setError('⚠️ Model is not responding. Please try ending and restarting the session.');
    }, CHECK_INTERVAL_MS);

    return () => window.clearInterval(interval);
  }, [status]);

  // Credit timer - set up warning and auto-end based on available credits
  useEffect(() => {
    if (status !== 'connected' || !initialCredits) return;

    // Calculate total available time in milliseconds (1 credit = 30 seconds)
    const totalAvailableTimeMs = initialCredits * 30 * 1000;
    
    // Show warning 1 minute (60 seconds) before time runs out
    const warningTimeMs = totalAvailableTimeMs - 60 * 1000;
    
    // Only set warning if we have more than 1 minute of time
    if (warningTimeMs > 0) {
      lowCreditWarningTimerRef.current = window.setTimeout(() => {
        setShowLowCreditWarning(true);
        console.log('⚠️ Low credit warning triggered - 1 minute remaining');
      }, warningTimeMs);
    }
    
    // Auto-end the session when time runs out
    autoEndTimerRef.current = window.setTimeout(() => {
      console.log('⏱️ Time limit reached - auto-ending session');
      endReasonRef.current = 'credits_exhausted';
      handleEnd();
    }, totalAvailableTimeMs);

    return () => {
      if (lowCreditWarningTimerRef.current) {
        clearTimeout(lowCreditWarningTimerRef.current);
        lowCreditWarningTimerRef.current = null;
      }
      if (autoEndTimerRef.current) {
        clearTimeout(autoEndTimerRef.current);
        autoEndTimerRef.current = null;
      }
    };
  }, [status, initialCredits]);

  // Push-to-Talk keyboard handler (Spacebar)
  useEffect(() => {
    if (status !== 'connected') return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Only activate PTT on Spacebar and prevent default scrolling
      if (e.code === 'Space' && !e.repeat) {
        e.preventDefault();
        if (isRecoveryUiLockRef.current) {
          return;
        }
        setIsPTTActive(true);
        isPTTActiveRef.current = true;
        
        // Start speech recognition for transcript capture
        startSpeechRecognition();
        
        // Signal start of activity to Gemini API
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          console.log('⌨️ Spacebar Pressed - Signaling activity start to Gemini');
          sendWsJson(wsRef.current, {
            realtimeInput: {
              activityStart: {}
            }
          });
        }
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        e.preventDefault();
        const wasActive = isPTTActiveRef.current;
        setIsPTTActive(false);
        isPTTActiveRef.current = false;
        if (!wasActive) {
          return;
        }
        
        // Stop speech recognition
        stopSpeechRecognition();
        
        // Signal end of activity to Gemini API so it can start generating response
        if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          console.log('⌨️ Spacebar Released - Signaling activity end to Gemini');
          sendWsJson(wsRef.current, {
            realtimeInput: {
              activityEnd: {}
            }
          });
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [status]);

  // Cleanup on unmount - only clean up resources, don't call onEnd
  useEffect(() => {
    return () => {
      shouldStayConnectedRef.current = false;
      isReconnectingRef.current = false;
      clearReconnectTimer();
      clearResumptionUpdateTimer();

      // Clear credit timers
      if (lowCreditWarningTimerRef.current) {
        clearTimeout(lowCreditWarningTimerRef.current);
        lowCreditWarningTimerRef.current = null;
      }
      if (autoEndTimerRef.current) {
        clearTimeout(autoEndTimerRef.current);
        autoEndTimerRef.current = null;
      }

      // Stop speech recognition
      stopSpeechRecognition();

      // Only clean up WebSocket and audio resources, DON'T call onEnd()
      // onEnd() should only be called when user explicitly ends the session
      if (wsRef.current) {
        lastCloseInitiatorRef.current = 'client';
        wsRef.current.close();
        wsRef.current = null;
      }
      stopAudioCapture();
    };
  }, []);

  return (
    <div className="w-full max-w-2xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-card p-8"
      >
        {/* Status Indicator */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div
              className={`w-3 h-3 rounded-full ${
                status === 'connected'
                  ? 'animate-pulse'
                  : ''
              }`}
              style={{
                background: status === 'connected' ? '#10b981' : 'rgba(255, 255, 255, 0.3)'
              }}
            />
            <span className="text-sm font-medium" style={{ color: 'var(--text)' }}>
              {status === 'connected' && 'Session Active'}
              {status === 'connecting' && 'Connecting...'}
              {status === 'disconnected' && 'Ready to Start'}
            </span>
          </div>
          {status === 'connected' && (
            <div className="flex items-center gap-2 px-3 py-1 rounded-lg" style={{ 
              background: 'rgba(110, 231, 255, 0.12)',
              border: '1px solid rgba(110, 231, 255, 0.2)'
            }}>
              <span className="text-xs font-medium" style={{ color: 'var(--muted)' }}>
                Duration:
              </span>
              <span className="text-sm font-mono font-semibold" style={{ 
                background: 'var(--accent)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                {formatTime(elapsedTime)}
              </span>
            </div>
          )}
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-4 p-3 rounded-xl" style={{ 
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.3)'
          }}>
            <p className="text-sm" style={{ color: '#ef4444' }}>{error}</p>
          </div>
        )}

        {/* Reconnect status (non-blocking) */}
        {reconnectNotice && status === 'connected' && (
          <div className="mb-4 p-3 rounded-xl" style={{
            background: 'rgba(59, 130, 246, 0.12)',
            border: '1px solid rgba(59, 130, 246, 0.35)'
          }}>
            <p className="text-sm" style={{ color: '#93c5fd' }}>{reconnectNotice}</p>
          </div>
        )}

        {/* Low Credit Warning */}
        {showLowCreditWarning && status === 'connected' && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4 p-3 rounded-xl"
            style={{ 
              background: 'rgba(251, 191, 36, 0.15)',
              border: '1px solid rgba(251, 191, 36, 0.4)'
            }}
          >
            <p className="text-sm font-medium" style={{ color: '#fbbf24' }}>
              ⚠️ Your credits are almost used up. This call will end in about one minute.
            </p>
          </motion.div>
        )}

        {/* Main Controls */}
        <div className="flex flex-col items-center gap-6">
          {status === 'disconnected' && (
            <motion.button
              whileHover={{ scale: 1.02, boxShadow: '0 12px 40px rgba(140, 150, 255, 0.35)' }}
              whileTap={{ scale: 0.98 }}
              onClick={handleStart}
              className="btn-gradient flex items-center gap-3 px-8 py-4 rounded-xl font-semibold text-base"
            >
              <Phone className="w-5 h-5" />
              Start Roleplay Session
            </motion.button>
          )}

          {status === 'connecting' && (
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="w-12 h-12 animate-spin" style={{ 
                color: '#6ee7ff'
              }} />
              <p style={{ color: 'var(--muted)' }}>
                Connecting to Gemini Live...
              </p>
            </div>
          )}

          {status === 'connected' && (
            <div className="flex flex-col items-center gap-6 w-full">
              {/* Voice Powered Orb Visualization */}
              <div className="relative w-64 h-64">
                <VoicePoweredOrb
                  enableVoiceControl={!isRecoveryUiLock && isPTTActive}
                  hue={0}
                  voiceSensitivity={1.8}
                  maxRotationSpeed={1.5}
                  maxHoverIntensity={1.0}
                />
              </div>

              {/* PTT Instruction */}
              <div className="text-center px-4 py-2 rounded-lg" style={{ 
                background: isRecoveryUiLock ? 'rgba(59, 130, 246, 0.12)' : 'rgba(110, 231, 255, 0.1)',
                border: isRecoveryUiLock ? '1px solid rgba(59, 130, 246, 0.35)' : '1px solid rgba(110, 231, 255, 0.2)'
              }}>
                <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>
                  {isRecoveryUiLock
                    ? (recoveryPhase === 'replay_context'
                      ? 'AI is restoring context. Please wait...'
                      : 'AI is thinking. Please wait...')
                    : (
                      <>
                        Hold <kbd className="px-2 py-1 rounded text-xs font-mono" style={{
                          background: 'rgba(110, 231, 255, 0.2)',
                          border: '1px solid rgba(110, 231, 255, 0.3)'
                        }}>SPACE</kbd> or the button below to speak
                      </>
                    )}
                </p>
              </div>

              {/* Control Buttons */}
              <div className="flex flex-col items-stretch gap-4 w-full max-w-sm">
                <motion.button
                  whileHover={isRecoveryUiLock ? undefined : { scale: 1.02 }}
                  whileTap={isRecoveryUiLock ? undefined : { scale: 0.98 }}
                  onMouseDown={handlePTTStart}
                  onMouseUp={handlePTTEnd}
                  onMouseLeave={handlePTTEnd}
                  onTouchStart={handlePTTStart}
                  onTouchEnd={handlePTTEnd}
                  disabled={isRecoveryUiLock}
                  className="flex items-center justify-center gap-2 px-10 py-5 rounded-2xl font-semibold text-lg transition-all w-full"
                  style={{
                    background: isRecoveryUiLock
                      ? 'rgba(148, 163, 184, 0.25)'
                      : isPTTActive 
                      ? 'linear-gradient(135deg, #10b981, #059669)' 
                      : 'rgba(110, 231, 255, 0.15)',
                    border: isRecoveryUiLock
                      ? '2px solid rgba(148, 163, 184, 0.45)'
                      : isPTTActive
                      ? '2px solid #10b981'
                      : '2px solid rgba(110, 231, 255, 0.3)',
                    color: isRecoveryUiLock ? '#cbd5e1' : isPTTActive ? '#fff' : '#6ee7ff',
                    boxShadow: isPTTActive ? '0 8px 24px rgba(16, 185, 129, 0.4)' : 'none',
                    userSelect: 'none',
                    cursor: isRecoveryUiLock ? 'not-allowed' : 'pointer',
                    opacity: isRecoveryUiLock ? 0.9 : 1
                  }}
                >
                  <Mic className="w-6 h-6" />
                  {isRecoveryUiLock ? 'Please Wait...' : (isPTTActive ? 'Speaking...' : 'Push to Talk')}
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.02, boxShadow: '0 8px 24px rgba(239, 68, 68, 0.3)' }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleEnd}
                  className="flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-medium transition-all w-full"
                  style={{
                    background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                    color: '#fff'
                  }}
                >
                  <PhoneOff className="w-5 h-5" />
                  End Session
                </motion.button>
              </div>
            </div>
          )}
        </div>

        {/* Info Text */}
        <div className="mt-8 text-center">
          <p className="text-sm" style={{ color: 'var(--muted)' }}>
            {status === 'connected'
              ? 'Hold the button or Spacebar to speak with the AI patient. Release to let the AI respond.'
              : 'Click the button above to begin your medical training roleplay session with Gemini Live.'}
          </p>
        </div>
      </motion.div>

      {/* Persistent Error Display - Always visible at bottom */}
      {persistentError && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 p-4 rounded-xl"
          style={{
            background: 'rgba(239, 68, 68, 0.12)',
            border: '2px solid rgba(239, 68, 68, 0.4)',
            boxShadow: '0 8px 24px rgba(239, 68, 68, 0.2)'
          }}
        >
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h4 className="text-sm font-semibold mb-1" style={{ color: '#ef4444' }}>
                Connection Error Log:
              </h4>
              <p className="text-xs font-mono break-all" style={{ color: '#f87171' }}>
                {persistentError}
              </p>
            </div>
            <button
              onClick={() => setPersistentError(null)}
              className="text-sm font-medium px-2 py-1 rounded transition-all"
              style={{ 
                color: '#ef4444',
                background: 'transparent'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              Clear
            </button>
          </div>
        </motion.div>
      )}
    </div>
  );
}


